/**
 * PackHeroStage — the workbench-as-hero for pack landing pages.
 *
 * Reference: thinking/design/handoffs/wb-final.jsx (sizes/copy/behavior).
 *
 * Architecture: this component is a VIEW only. PackLandingTemplate decides the stage from the
 * visitor's state (`packStageFor`) and, for an owner, hands down the server-resolved run's slots.
 * Nothing the visitor does on the stage moves it between stages.
 *
 *   ad / no_model   — the pack's curated nine, paged by the card; the primary opens the model intake
 *   waiting_model   — the same nine under the training bar; the left column carries the queue CTA
 *   model_ready     — the owner's entry: the nine and the Generate action
 *   buying          — the owner's run in progress: slots fill in as they land
 *   done            — the owner's delivered run
 *
 * The pager's stepping arithmetic and gesture handling come from `@core/packs/stage/` (plan
 * 2026_09_23_pack_stage_coda.md). Narcis adopts the coda there (plan 2026_09_25_narcis_pack_coda.md):
 * on the showcase stages (`SHOWCASE_STAGES`) the nine looks end on a tenth frame holding the model
 * path's action (`coda`, built by the template), and each look carries the look door to the wall armed
 * on that picture (`lookDoor`).
 */

import {useEffect, useRef, useState} from 'react'
import {narcisTrack} from '@product/narcis/utils/analytics'
import {MIN_TRAINING_PHOTOS, MAX_TRAINING_PHOTOS} from '@core/hooks/useModelIntake'
import type {DSlotState} from '@core/packs/dSubState'
import type {LivePackDefinition, SerializedUserModel} from './types'
import styles from './PackHeroStage.module.scss'
import {SHOWCASE_STAGES, type PackStage} from './packStage'
import {pagerLookIndices} from './pagerLooks'
import {codaIndex, nextLookIndex, withCoda} from '@core/packs/stage/pager'
import {usePagerGestures} from '@core/packs/stage/usePagerGestures'
import {StageCoda} from '@core/packs/stage/StageCoda'
import {CodaThumb} from '@core/packs/stage/CodaThumb'
import {LookDoor} from '@core/packs/stage/LookDoor'

// ── Types ────────────────────────────────────────────────────────────────────

/** The coda's words and its one action — the same press as the page's primary CTA. */
export interface StageCodaAction {
    lead: string
    head: string
    sub: string
    label: string
    disabled: boolean
    onPress: () => void
}

/** The look door: where look `i` goes on the wall (null → no door on that look). */
export interface StageLookDoor {
    label: string
    hrefAt: (i: number) => string | null
    onFollow: (i: number) => void
}

interface PackHeroStageProps {
    pack: LivePackDefinition
    /** The no-model verb — the canon's, priced while payment is asked (`proposition.ts`, chosen by
     *  the template). */
    createVerb: string
    stage: PackStage
    /** The owner's run, slot order. Absent outside the owner's stages. */
    slots?: readonly DSlotState[]
    /** The owner's Generate action (`model_ready`). */
    onGenerate: () => void
    /** Owner-only: re-run the same pack from the trained model. */
    onRegenerate?: () => void
    /** The delivered run's primary: download every rendered photo. */
    onDoneCta?: () => void
    /** The delivered run's "yours · rendered {date}" stage chip — pre-formatted by the caller
     *  (PackLandingTemplate owns `formatBannerDate`). */
    ownerRunDate?: string
    /** The signed-in user's trained model, if any (the training bar reads it). */
    userModel?: SerializedUserModel | null
    /** Opens the model intake on this page — every "Create my model" on the stage. */
    onTrain: () => void
    /** The tenth frame's content; null keeps the stage at its looks. */
    coda?: StageCodaAction | null
    lookDoor?: StageLookDoor | null
}

// Display-only derivation of "checkpoint {k} of {K}" from the raw step counts (copy spec §3
// state C trainbar). Mirrors the trainer's own checkpoint-every-50-steps cadence — a display
// concern, not a model concern, so it lives here rather than in trainingShift.server.ts.
const CHECKPOINT_INTERVAL = 50

/** The ready owner's primary CTA — the wall's own verb (operator, 2026-09-25: "let's not make it
 *  cryptic and simply Generate"). An owner has paid for the model: nothing on a pack is priced. */
export const OWNER_GENERATE_LABEL = 'Generate'

const NO_SLOTS: readonly DSlotState[] = []

// ── Helper: find the slot index of the highest-completed slot ─────────────────
// "Latest done" = the highest-index slot in 'done' status. Drives big-area auto-follow.

function highestDoneSlotIndex(slots: readonly DSlotState[]): number {
    let highest = -1
    for (let i = 0; i < slots.length; i++) {
        if (slots[i].status === 'done') highest = i
    }
    return highest
}

// ── Lock icon SVG ─────────────────────────────────────────────────────────────

function LockIcon() {
    return (
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
    )
}

// ── Main component ────────────────────────────────────────────────────────────

export function PackHeroStage({
    pack,
    createVerb,
    stage,
    slots = NO_SLOTS,
    onGenerate,
    onRegenerate,
    onDoneCta,
    ownerRunDate,
    userModel,
    onTrain,
    coda = null,
    lookDoor = null,
}: PackHeroStageProps) {
    const nImages = pack.grid.nImages

    // Showcase samples — the pack's nine curated portraits (3 looks × 3), browsed in the big area
    // and from the thumb strip. pack.rows is exactly three rows of three; flattened = nine.
    const showcaseImages = pack.rows.flatMap((r) => r.portraits)
    const isShowcaseStage = SHOWCASE_STAGES.includes(stage)

    // Big-area featured index: auto-follows the highest completed slot.
    const [featuredIdx, setFeaturedIdx] = useState(0)
    // A strip click pins the big area to that frame, so a user reading one shot mid-run keeps it
    // while later slots land. The pin releases once no slot is done — a board with nothing on it
    // is a fresh run, which should auto-follow again.
    const pinnedByUserRef = useRef(false)
    const highestDone = highestDoneSlotIndex(slots)
    useEffect(() => {
        if (highestDone < 0) {
            pinnedByUserRef.current = false
            return
        }
        if (pinnedByUserRef.current) return
        setFeaturedIdx(highestDone)
    }, [highestDone])

    // After-label bottom-right
    const afterLabel = (() => {
        if (stage === 'model_ready') return 'AFTER · from your model'
        // "look {i} of {n}": one delivered image.
        return `photo ${featuredIdx + 1} of ${nImages}`
    })()

    // Status chip text
    const statusChipText = (() => {
        if (stage === 'ad') return `${nImages} photos · ${pack.rows.length} scenes`
        if (stage === 'model_ready') return `${nImages} photos · from your model`
        if (stage === 'no_model') return 'no model yet'
        if (stage === 'waiting_model') {
            return `creating your model${userModel?.percent != null ? ` · ${userModel.percent}%` : ''}`
        }
        if (stage === 'buying') {
            const done = slots.filter((s) => s.status === 'done').length
            return `working · ${done} of ${nImages}`
        }
        return ownerRunDate ? `yours · made ${ownerRunDate}` : `delivered · ${nImages}/${nImages}`
    })()

    // No pulse dot at no_model — nothing is generating until the visitor starts training.
    const showPulseDot = stage === 'buying' || stage === 'waiting_model'

    // Polite live region: text changes are read aloud. Decorative chrome stays aria-hidden.
    const liveAnnouncement = (() => {
        if (stage === 'model_ready') return `Get all ${nImages} photos from your model`
        if (stage === 'no_model') return `Create your model to get all ${nImages} photos`
        if (stage === 'waiting_model') return 'Creating your model: your photos arrive when it is ready'
        if (stage === 'buying') return 'Working on your pack'
        if (stage === 'done') return `All ${nImages} photos delivered`
        return ''
    })()

    // Big area image: the curated sample, or the owner's run slot.
    const bigAreaSlot = slots[featuredIdx]
    const bigAreaImageUrl = (() => {
        // Leading with the first of the nine (= cover_portrait, which is what og:image / JSON-LD /
        // the image sitemap already nominate as representative) keeps the most prominent
        // above-the-fold <img> aligned with the crawled corpus, so Google Images can't prefer an
        // off-sitemap hero. cover_portrait remains the final fallback.
        if (isShowcaseStage) return showcaseImages[featuredIdx] ?? pack.cover_portrait
        return bigAreaSlot?.status === 'done' ? bigAreaSlot.imageUrl : null
    })()

    const bigAreaAlt = isShowcaseStage
        ? `Example portrait from the ${pack.title}`
        : `Portrait ${featuredIdx + 1} of ${nImages}`

    const ctaLabel = (() => {
        if (stage === 'ad' || stage === 'no_model') return createVerb
        if (stage === 'model_ready') return OWNER_GENERATE_LABEL
        if (stage === 'waiting_model') return 'Creating your model…'
        if (stage === 'buying') return 'Working · they land one by one'
        return 'Download your portraits'
    })()

    const ctaDisabled = stage === 'buying' || stage === 'waiting_model'

    function handleCTAClick() {
        if (stage === 'ad' || stage === 'no_model') {
            // Nothing downstream reports this tap, so the stage does.
            narcisTrack('model_cta_click', {source: 'pack_gate'})
            onTrain()
            return
        }
        if (stage === 'model_ready') {
            onGenerate()
            return
        }
        if (stage === 'done') onDoneCta?.()
    }

    // A locked/streaming thumb's slot_status for pack_look_locked_tapped — read straight off the
    // slot, independent of the per-render booleans the JSX below computes (this runs from a click).
    function lockedTapSlotStatus(slot: DSlotState | undefined): 'locked' | 'streaming' | 'absent' {
        if (!slot) return 'absent'
        return slot.status === 'streaming' ? 'streaming' : 'locked'
    }

    // Strip thumb click — user selects a completed shot as featured. Routes through goToLook so the
    // strip is the pager's fourth input path in the data as well as in behaviour. A non-done slot
    // fires pack_look_locked_tapped instead of advancing — see that event's own doc in
    // @core/utils/analytics.ts for why it isn't a pack_look_advanced variant.
    function handleThumbClick(i: number) {
        const slot = slots[i]
        if (!slot || slot.status !== 'done') {
            narcisTrack('pack_look_locked_tapped', {
                pack_slug: pack.slug,
                look_index: i,
                visual_stage: stage,
                slot_status: lockedTapSlotStatus(slot),
            })
            return
        }
        goToLook(i, 'thumb')
    }

    // ── Card as pager (designer delta §5) ───────────────────────────────────────────────────
    // The big image is the browsing control for the nine looks — and browsing it is what teaches the
    // strip below, since the active border follows every advance. The two zones are real buttons
    // rather than a click handler on the image: a keyboard visitor needs somewhere to land, and a
    // named control is the only thing a screen reader can announce for a surface whose whole
    // affordance is "the picture is the button".
    const lookIndices = pagerLookIndices({
        stage,
        sampleCount: showcaseImages.length,
        slotStatuses: Array.from({length: nImages}, (_, i) => slots[i]?.status),
    })
    // The coda joins only a stage paging the curated nine — never a run's slots.
    const codaIdx = codaIndex(showcaseImages.length)
    const hasCoda = coda !== null && isShowcaseStage && lookIndices.length > 0
    const pagerIndices = hasCoda ? withCoda(lookIndices, codaIdx) : lookIndices
    const onCoda = hasCoda && featuredIdx === codaIdx
    const doorHref = lookDoor && isShowcaseStage && lookIndices.length > 0 && !onCoda ? lookDoor.hrefAt(featuredIdx) : null

    // Movement is one function so every input path — zone, swipe, key, thumb — lands on the same
    // wrap arithmetic and the same event.
    function goToLook(idx: number, method: 'tap' | 'swipe' | 'arrow' | 'thumb') {
        // Pin for the reason a thumb click pins: once the viewer has picked a frame, a slot landing
        // behind them must not pull the card away mid-read.
        pinnedByUserRef.current = true
        setFeaturedIdx(idx)
        narcisTrack('pack_look_advanced', {
            pack_slug: pack.slug,
            method,
            look_index: idx,
            visual_stage: stage,
        })
    }

    function advanceLook(delta: 1 | -1, method: 'tap' | 'swipe' | 'arrow') {
        const next = nextLookIndex(pagerIndices, featuredIdx, delta)
        if (next === null) return
        goToLook(next, method)
    }

    // Swipe (axis-tested, with the synthesized-click suppression), ←/→ and zone clicks — the one
    // gesture layer every house's card uses (`@core/packs/stage/usePagerGestures`).
    const pagerGestures = usePagerGestures(advanceLook)

    return (
        <section className={styles.stage} aria-label="Sample portraits from this pack">
            {/* Trainbar — copy spec §3 state C, ABOVE the (dimmed) big area, not inside it
                (canvas 02-C: `.trainbar` and `.big.dim` are siblings within `.stage`). Live % from
                the existing checkpoint signal (userModel.percent); checkpoint {k} of {K} derived
                from the raw step counts (CHECKPOINT_INTERVAL mirrors the trainer's own 50-step
                save cadence — see trainingShift.server.ts's UserModel doc). */}
            {stage === 'waiting_model' && userModel && (
                <div className={styles.trainbar}>
                    <div className={styles.trainbarRow}>
                        <b className={styles.trainbarStatus}>
                            <span className={styles.trainbarDot} aria-hidden="true" />
                            creating your model
                        </b>
                        <span className={styles.trainbarMeta}>
                            {userModel.percent != null && `${userModel.percent}% `}
                            {userModel.checkpointStep != null && userModel.targetSteps != null &&
                                `· checkpoint ${Math.round(userModel.checkpointStep / CHECKPOINT_INTERVAL)} of ${Math.round(userModel.targetSteps / CHECKPOINT_INTERVAL)}`}
                        </span>
                    </div>
                    <div className={styles.trainbarBar}>
                        <i style={{width: `${userModel.percent ?? 0}%`}} />
                    </div>
                    {userModel.thumbUrls.length > 0 && (
                        <div className={styles.trainbarSetRow}>
                            {userModel.thumbUrls.slice(0, 12).map((url, i) => (
                                <div key={url} className={styles.trainbarSetThumb}>
                                    <img src={url} alt="" />
                                    {i % 3 === 0 && <div className={`${styles.trainbarSetShimmer} ${styles.shimmer}`} aria-hidden="true" />}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Big portrait area — flex:1, portrait orientation */}
            <div className={styles.bigArea}>

                {bigAreaImageUrl !== null && (
                    <img
                        src={bigAreaImageUrl}
                        alt={bigAreaAlt}
                        className={styles.bigImage}
                        loading="eager"
                    />
                )}

                {/* Card as pager. Stacking: the zones sit under everything on the card that can be
                    clicked, and the overlays above them (chip, after-label, intro dissolve) are all
                    pointer-events:none. Below two reachable frames the zones don't render at all
                    rather than rendering two buttons that do nothing. */}
                {onCoda && coda && (
                    <div
                        className={styles.codaFill}
                        onTouchStart={pagerGestures.onTouchStart}
                        onTouchEnd={pagerGestures.onTouchEnd}
                        onKeyDown={pagerGestures.onKeyDown}
                    >
                        <StageCoda
                            backdrop={showcaseImages[showcaseImages.length - 1]}
                            ratio={{w: 3, h: 4}}
                            lead={coda.lead}
                            head={coda.head}
                            sub={coda.sub}
                        >
                            <button
                                type="button"
                                className={`${styles.ctaButton} ${styles.codaButton} ${coda.disabled ? styles.ctaButtonDisabled : styles.ctaButtonActive}`}
                                disabled={coda.disabled}
                                aria-disabled={coda.disabled}
                                onClick={coda.disabled ? undefined : coda.onPress}
                            >
                                {coda.label}
                            </button>
                        </StageCoda>
                    </div>
                )}

                {doorHref !== null && lookDoor && (
                    <LookDoor
                        href={doorHref}
                        label={lookDoor.label}
                        onFollow={() => lookDoor.onFollow(featuredIdx)}
                    />
                )}

                {!onCoda && pagerIndices.length > 1 && (
                    <div
                        className={styles.pager}
                        onTouchStart={pagerGestures.onTouchStart}
                        onTouchEnd={pagerGestures.onTouchEnd}
                        onKeyDown={pagerGestures.onKeyDown}
                    >
                        <button
                            type="button"
                            className={`${styles.pagerZone} ${styles.pagerZonePrev}`}
                            onClick={() => pagerGestures.onZoneClick(-1)}
                            aria-label="previous look"
                        >
                            <span className={styles.pagerArrow} aria-hidden="true">←</span>
                        </button>
                        <button
                            type="button"
                            className={`${styles.pagerZone} ${styles.pagerZoneNext}`}
                            onClick={() => pagerGestures.onZoneClick(1)}
                            aria-label="next look"
                        >
                            <span className={styles.pagerArrow} aria-hidden="true">→</span>
                        </button>
                    </div>
                )}

                {/* Status chip — top-right */}
                {!onCoda && (
                    <div className={styles.statusChip} aria-hidden="true">
                        {showPulseDot && (
                            <span className={styles.pulseDot} aria-hidden="true" />
                        )}
                        {statusChipText}
                    </div>
                )}

                {/* After label — bottom-right, on the owner's stages only: the showcase promises no
                    before, and a look door takes the corner when one is offered. */}
                {(stage === 'model_ready' || stage === 'buying' || stage === 'done') && doorHref === null && !onCoda && (
                    <div className={styles.afterLabel} aria-hidden="true">
                        {afterLabel}
                    </div>
                )}

                {/* Intro dissolve overlay (ad stage only). Animates a brief reveal dissolve when the
                    stage mounts. Gated: prefers-reduced-motion skips the animation (CSS rule in
                    SCSS). The overlay fades from a solid surface color to transparent, revealing
                    the showcase portrait beneath. */}
                {stage === 'ad' && (
                    <div className={`${styles.introDissolve} ${styles.introDissolveAnimate}`} aria-hidden="true" />
                )}
            </div>{/* end bigArea */}

            {/* Thumb strip. Showcase stages: a clickable strip of the nine pack samples, browsed in
                the big area. Owner's run: the live slots. */}
            {isShowcaseStage ? (
                <div className={styles.thumbStrip} role="list" aria-label={`${nImages} sample photos. Tap to see each`}>
                    {showcaseImages.map((src, i) => {
                        const isActive = i === featuredIdx
                        return (
                            <button
                                key={i}
                                type="button"
                                className={[styles.thumb, styles.thumbDone, isActive ? styles.thumbActive : '']
                                    .filter(Boolean).join(' ')}
                                // The strip is the pager's fourth input path, so it reports through
                                // the same event (delta §9 — the tap/swipe/arrow/thumb shares are
                                // only comparable if the thumbs are counted too). `visual_stage` on
                                // the event isolates the anonymous entry.
                                onClick={() => goToLook(i, 'thumb')}
                                aria-label={`See sample ${i + 1} of ${nImages}`}
                                aria-pressed={isActive}
                                role="listitem"
                            >
                                <img
                                    src={src}
                                    alt=""
                                    className={`${styles.thumbImage} ${styles.thumbImageDone}`}
                                    draggable={false}
                                />
                            </button>
                        )
                    })}
                    {hasCoda && coda && (
                        <CodaThumb
                            className={styles.thumb}
                            on={onCoda}
                            label={`${coda.lead} ${coda.head}`}
                            onSelect={() => goToLook(codaIdx, 'thumb')}
                            role="listitem"
                        />
                    )}
                </div>
            ) : (
                <div className={styles.thumbStrip} role="list" aria-label="Your looks">
                    {Array.from({length: nImages}, (_, i) => {
                        const slot = slots[i]
                        const isDone = slot?.status === 'done'
                        const isStreaming = slot?.status === 'streaming'
                        const isActive = i === featuredIdx
                        const isLocked = !slot || slot.status === 'pending'

                        return (
                            <button
                                key={i}
                                type="button"
                                className={[
                                    styles.thumb,
                                    isActive ? styles.thumbActive : '',
                                    isStreaming ? styles.thumbStreaming : '',
                                    isDone ? styles.thumbDone : '',
                                    isLocked ? styles.thumbLocked : '',
                                ].filter(Boolean).join(' ')}
                                onClick={() => handleThumbClick(i)}
                                aria-label={
                                    isDone
                                        ? `View look ${i + 1} of ${nImages}`
                                        : `Look ${i + 1}: ${isLocked ? 'locked' : 'working'}`
                                }
                                aria-disabled={!isDone}
                                aria-pressed={isActive && isDone}
                                role="listitem"
                            >
                                {isLocked && (
                                    <>
                                        <div className={styles.thumbLockedOverlay} aria-hidden="true" />
                                        <span className={styles.thumbLockIcon} aria-hidden="true">
                                            <LockIcon />
                                        </span>
                                    </>
                                )}

                                {isStreaming && (
                                    <div className={styles.thumbShimmer} aria-hidden="true" />
                                )}

                                {isDone && (
                                    <img
                                        src={slot.imageUrl}
                                        alt=""
                                        className={`${styles.thumbImage} ${styles.thumbImageDone}`}
                                        draggable={false}
                                    />
                                )}

                                {/* Shimmer base for queued thumbs while the run is rendering. */}
                                {isLocked && stage === 'buying' && (
                                    <div className={styles.thumbShimmer} aria-hidden="true" />
                                )}
                            </button>
                        )
                    })}
                </div>
            )}

            {/* CTA bar — below the stage, above the strip. */}
            <div className={styles.ctaBar}>
                {/* waiting_model (copy spec §3 state C): no CTA button in the stage at all — the
                    queue chip/CTA/ghost/note live in the left column, which on mobile reorders
                    below the stage (same DOM, CSS order) so there is exactly one queue CTA per
                    breakpoint, not a duplicate disabled one here. */}
                {stage !== 'waiting_model' && (
                    <button
                        type="button"
                        className={[
                            styles.ctaButton,
                            ctaDisabled ? styles.ctaButtonDisabled : styles.ctaButtonActive,
                            stage === 'done' ? styles.ctaButtonDone : '',
                        ].filter(Boolean).join(' ')}
                        disabled={ctaDisabled}
                        aria-disabled={ctaDisabled}
                        onClick={ctaDisabled ? undefined : handleCTAClick}
                    >
                        {stage === 'buying' && (
                            <span className={styles.pulseDot} aria-hidden="true" />
                        )}
                        {ctaLabel}
                    </button>
                )}

                {/* Sub-line note — waiting_model's text lives in the left column on every breakpoint. */}
                {stage !== 'waiting_model' && (
                    <p className={styles.ctaNote}>
                        {(stage === 'ad' || stage === 'no_model') && `${MIN_TRAINING_PHOTOS}–${MAX_TRAINING_PHOTOS} photos of yourself · we email you when your model is ready`}
                        {stage === 'model_ready' && `from your model · all ${nImages} arrive now`}
                        {stage === 'buying' && 'saved to My Packs · pick up where you left off'}
                        {stage === 'done' && `all ${nImages} delivered · saved to My Packs`}
                    </p>
                )}

                {/* Copy spec §3c ("quiet note-level action"): the delivered run's secondary action,
                    following the pack page's own D-del note. */}
                {stage === 'done' && onRegenerate && (
                    <button
                        type="button"
                        className={styles.ctaGhost}
                        onClick={onRegenerate}
                    >
                        Make this pack again →
                    </button>
                )}

                {/* Polite live region for screen reader state announcements. Always rendered (never
                    conditionally mounted) so text changes are picked up by the live region observer.
                    Visually hidden. */}
                <span
                    role="status"
                    aria-live="polite"
                    aria-atomic="true"
                    className={styles.srOnly}
                >
                    {liveAnnouncement}
                </span>
            </div>

        </section>
    )
}
